import * as faceapi from '@vladmandic/face-api';
import recognitionManifest from '@vladmandic/face-api/model/face_recognition_model-weights_manifest.json';
import recognitionModelUrl from '@vladmandic/face-api/model/face_recognition_model.bin?url';

const MODEL_URL = '/models';
let modelsPromise: Promise<void> | null = null;

const loadModels = () => {
  if (!modelsPromise) {
    modelsPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      fetch(recognitionModelUrl)
        .then(response => {
          if (!response.ok) throw new Error('The face recognition model could not be loaded.');
          return response.arrayBuffer();
        })
        .then(buffer => {
          const specs = recognitionManifest.flatMap(group => group.weights);
          faceapi.nets.faceRecognitionNet.loadFromWeightMap(
            faceapi.tf.io.decodeWeights(buffer, specs as Parameters<typeof faceapi.tf.io.decodeWeights>[1]),
          );
        }),
    ]).then(() => undefined).catch(error => {
      modelsPromise = null;
      throw error;
    });
  }
  return modelsPromise;
};

const loadImage = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(url);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('The selected image could not be read.'));
  };
  image.src = url;
});

/** Generates an in-memory descriptor; callers must submit it immediately and never persist it. */
export const generateFaceEmbedding = async (file: File): Promise<string> => {
  await loadModels();
  const image = await loadImage(file);
  const result = await faceapi
    .detectSingleFace(image, new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.5 }))
    .withFaceLandmarks()
    .withFaceDescriptor();

  if (!result) {
    throw new Error('NO_FACE_DETECTED');
  }
  return JSON.stringify(Array.from(result.descriptor));
};
